import api from '@/api'
import { STORAGE_KEYS, TIMEOUTS, HTTP_STATUS } from '@/constants'
import logger from '@/utils/logger'

function getStoredToken() {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
    if (token && typeof token === 'string' && token.split('.').length === 3) {
      return token
    }
    if (token) {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
    }
    return null
  } catch (error) {
    return null
  }
}

const state = {
  token: getStoredToken(),
  user: JSON.parse(localStorage.getItem(STORAGE_KEYS.USER) || 'null'),
  isInitializing: false
}

const getters = {
  isAuthenticated: state => !!state.token,
  currentUser: state => state.user,
  authToken: state => state.token,
  isInitializing: state => state.isInitializing,
  isAdmin: state => {
    if (!state.user) return false
    return state.user.roles === 'admin'
  },
  isResearcher: state => {
    if (!state.user) return false
    return !!(state.user.registration_status || (state.user.id && !state.user.roles))
  }
}

const mutations = {
  setInitializing(state, status) {
    state.isInitializing = status
  },
  setAuth(state, { token, user }) {
    state.token = token
    state.user = user
    try {
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token)
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
    } catch (error) {
      logger.warn('Failed to save auth data to localStorage', { error: error.message })
    }
  },
  clearAuth(state) {
    state.token = null
    state.user = null
    try {
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
      localStorage.removeItem(STORAGE_KEYS.USER)
      // Also clear any Cognito-specific remnants just in case
      localStorage.removeItem(STORAGE_KEYS.RESEARCHER_ID_TOKEN)
      localStorage.removeItem(STORAGE_KEYS.RESEARCHER_REFRESH_TOKEN)
      localStorage.removeItem(STORAGE_KEYS.RESEARCHER_ACCESS_TOKEN)
      localStorage.removeItem(STORAGE_KEYS.CURRENT_RESEARCHER)
    } catch (error) {
      logger.warn('Failed to remove auth data from localStorage', { error: error.message })
    }
  }
}

const actions = {
  async initializeAuth({ commit, state, dispatch, getters }) {
    if (state.token) {
      commit('setInitializing', true)
      try {
        if (state.token && !state.user) {
          const isResearcher = localStorage.getItem(STORAGE_KEYS.RESEARCHER_ID_TOKEN) ||
            window.location.pathname.includes('/researcher/') ||
            window.location.pathname.includes('/patient-summary/')

          if (isResearcher) {
            await dispatch('researcher/getProfile', null, { root: true })
          } else {
            await dispatch('fetchMe')
          }
        } else if (getters.isResearcher) {
          // Verify researcher session and refresh profile
          await dispatch('researcher/getProfile', null, { root: true })
        } else {
          // Verify standard user session
          await dispatch('fetchMe')
        }
      } catch (error) {
        const status = error.response?.status
        if (status === HTTP_STATUS.UNAUTHORIZED) {
          logger.info('Session expired, clearing auth state')
          commit('clearAuth')
        } else {
          logger.error('Auth initialization failed', { error: error.message })
        }
      } finally {
        commit('setInitializing', false)
      }
    }
  },
  async fetchMe({ commit, state }) {
    const response = await api.get('/api/me', {
      headers: {
        'Authorization': `Bearer ${state.token}`
      },
      timeout: TIMEOUTS.AUTH_INIT
    })
    commit('setAuth', {
      token: state.token,
      user: response.data
    })
    return response.data
  },
  logout({ commit }) {
    commit('clearAuth')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

